/**
 * AnswerSheetLogic.ts
 * Handles the visual marking of questions in the Answer Sheet iframe.
 * Strictly matches legacy visual styles (EL_*.js).
 */

const ASSETS_BASE_URL = process.env.REACT_APP_ASSETS_BASE_URL || "/";

const ICONS = {
    Correct: `${ASSETS_BASE_URL}Image/AnswerSheet/CorrectAnswer.png`,
    Wrong: `${ASSETS_BASE_URL}Image/AnswerSheet/WrongAnswer.png`,
    Skipped: `${ASSETS_BASE_URL}Image/AnswerSheet/SkippedAnswer.png`,
    
    CorrectGif: `${ASSETS_BASE_URL}Image/Questions/EL_O/AnsCorrect.GIF`,
    WrongGif: `${ASSETS_BASE_URL}Image/Questions/EL_O/AnsWrong.GIF`,
    ShouldBeGif: `${ASSETS_BASE_URL}Image/Questions/EL_O/AnsShouldbe.GIF`
};

// Mapping for Type O Background Images
const TYPE_O_BGS: Record<number, { n: string; d: string }> = {
    2: { n: 'large_objective_n.png', d: 'large_objective_d.png' },
    3: { n: 'medium_objective_n.png', d: 'medium_objective_d.png' },
    4: { n: 'small_objective_n.png', d: 'small_objective_d.png' },
    5: { n: 'large_objective_shortened_n.png', d: 'large_objective_shortened_d.png' },
    6: { n: 'medium_objective_shortened_n.png', d: 'medium_objective_shortened_d.png' },
    7: { n: 'small_objective_shortened_n.png', d: 'small_objective_shortened_d.png' }
};

// Mapping for Type C Background Images
const TYPE_C_BGS: Record<number, { n: string; d: string }> = {
    2: { n: 'large_objective_n.png', d: 'large_objective_d.png' },
    3: { n: 'medium_objective_n.png', d: 'medium_objective_d.png' },
    4: { n: 'small_objective_n.png', d: 'small_objective_d.png' },
    5: { n: 'large_objective_shortened_n.png', d: 'large_objective_shortened_d.png' },
    6: { n: 'medium_objective_shortened_n.png', d: 'medium_objective_shortened_d.png' },
    7: { n: 'small_objective_shortened_n.png', d: 'small_objective_shortened_d.png' }
};

const removeElements = (doc: Document, ids: string[]) => {
    ids.forEach(id => {
        const el = doc.getElementById(id);
        if (el) el.remove();
    });
};

// --- HELPER: ROBUST PARSING FOR VALUES ---
const parseValuesRobust = (str: string) => {
    const map: Record<number, string> = {};
    if (!str) return map;

    if (str.includes("~") && /\d{2}/.test(str.substring(0, 2))) {
        str.split("~").forEach(s => {
            const match = s.match(/^(\d+)(.*)$/);
            if (match) {
                map[parseInt(match[1], 10)] = match[2];
            }
        });
    } else if (str.includes("|") || (!str.includes("~") && str.length > 0)) {
        const parts = str.split("|");
        parts.forEach((val, index) => {
            if (val !== "") map[index] = val;
        });
    }
    return map;
};

// --- HELPER: ROBUST PARSING FOR INDICES ---
const parseIndicesRobust = (str: string) => {
    const indices: number[] = [];
    if (!str) return indices;

    if (str.includes("~")) {
        str.split("~").forEach(s => {
            const match = s.match(/^(\d+)/);
            if (match) indices.push(parseInt(match[1], 10));
        });
    } else if (str.includes("|")) {
        const parts = str.split("|");
        parts.forEach((val, index) => {
            if (val !== "") indices.push(index);
        });
    } else {
        if(str !== "") indices.push(0);
    }
    return indices;
};

export const markQuestion = (
    doc: Document, 
    qType: string, 
    userAns: string, 
    wrongAns: string, 
    blankAns: string, 
    correctAnsStr: string
) => {
    if (!doc) return;

    const uCorrect = userAns || "";
    const uWrong = wrongAns || "";
    const uBlank = blankAns || "";

    switch (qType) {
        case 'O':
        case 'O_RADIO':
        case 'O_IMAGE':
            markTypeO(doc, uCorrect, uWrong, qType);
            break;
        case 'B':
            markTypeB(doc, uCorrect, uWrong);
            break;
        case 'F':
            markTypeF(doc, uCorrect, uWrong, uBlank);
            break;
        case 'N':
            markTypeN(doc, uCorrect, uWrong, uBlank);
            break;
        case 'W':
            markTypeW(doc, uCorrect, uWrong, uBlank, correctAnsStr);
            break;
        case 'C':
            markTypeC(doc, uCorrect, uWrong);
            break;
        case 'D':
            markTypeD(doc, uCorrect, uWrong);
            break;
        case 'M':
            markTypeM(doc, uCorrect, uWrong);
            break;
        case 'L':
            markTypeL(doc, uCorrect, uWrong);
            break;
        case 'I':
            markTypeI(doc, uCorrect, uWrong, uBlank);
            break;
        default:
            console.warn(`Marking logic for type ${qType} is not implemented.`);
    }
};

// ============================================================================
// TYPE O (Standard), O_RADIO, O_IMAGE
// ============================================================================
const markTypeO = (doc: Document, enteredCorrect: string, enteredWrong: string, qType: string) => {
    const rows = Array.from(doc.querySelectorAll("tr[id^='KSD_TR_']"));
    const infoVal = (doc.getElementById("KSD_MW_Info") as HTMLInputElement)?.value;
    const parts = infoVal ? infoVal.split("|") : [];
    
    const correctIndexStr = parts[3] || "0";
    let noOfOptionsToShow = 0;
    if (parts.length > 4) {
        noOfOptionsToShow = parseInt(parts[4], 10);
        if (isNaN(noOfOptionsToShow)) noOfOptionsToShow = 0;
    }
    const optionImageType = parts.length >= 9 ? parseInt(parts[8]) : 0;
    const isImageBased = optionImageType >= 2; 

    const rowData = rows.map((row) => {
        const radio = row.querySelector("input[id^='KSD_Rad_']") as HTMLInputElement;
        
        const idxStr = radio ? (radio.name || radio.getAttribute("name") || "") : "";
        const idxInt = parseInt(idxStr, 10);
        
        const valChar = isNaN(idxInt) ? "" : String.fromCharCode(65 + idxInt);

        return { row: row as HTMLElement, idx: idxStr, val: valChar };
    });

    if (noOfOptionsToShow > 0 && noOfOptionsToShow < rowData.length) {
        const rowsKeep = new Set<HTMLElement>();

        const correctRow = rowData.find(r => r.idx === correctIndexStr);
        if (correctRow) rowsKeep.add(correctRow.row);

        const userSelection = enteredCorrect || enteredWrong;
        if (userSelection) {
            const userRow = rowData.find(r => r.idx === userSelection || r.val === userSelection);
            if (userRow) rowsKeep.add(userRow.row);
        }

        rowData.forEach(item => {
            if (item.idx === "24" || item.idx === "25") rowsKeep.add(item.row);
        });

        for (const item of rowData) {
            if (rowsKeep.size >= noOfOptionsToShow) break;
            rowsKeep.add(item.row);
        }

        rowData.forEach(item => {
            item.row.style.display = rowsKeep.has(item.row) ? "" : "none";
        });
    }

    let bgNormal = "";
    if (isImageBased && TYPE_O_BGS[optionImageType]) {
        bgNormal = `url('${ASSETS_BASE_URL}Image/Questions/EL_O/${TYPE_O_BGS[optionImageType].n}')`;
    }

    let visibleRowIndex = 0; 

    rowData.forEach((item) => {
        const { row, idx, val } = item;
        if (row.style.display === "none") return;

        const radio = row.querySelector("input[id^='KSD_Rad_']") as HTMLInputElement;
        const labelCell = row.querySelector('td[id="KSD_TD1"]') as HTMLElement;
        const contentCell = row.querySelector('td[id="KSD_TD2"]') as HTMLElement;
        const optionCell = row.querySelector('td[id^="KSD_TD_"]') as HTMLElement;
        const ansCell = (row.querySelector("td[id^='KSD_TDANS_']") || row.querySelector("td[id^='TDANS_']")) as HTMLElement;

        if (isImageBased) {
            if (optionCell && bgNormal) {
                optionCell.style.backgroundImage = bgNormal;
                optionCell.style.backgroundRepeat = "no-repeat";
                optionCell.style.backgroundColor = "transparent";
                optionCell.style.backgroundSize = "100% 100%";
                optionCell.style.padding = "10px";
                optionCell.style.cursor = "default";
            }
        } else {
            const table = row.closest('table');
            if(table) {
                table.style.borderCollapse = "collapse"; 
                table.style.width = "100%";
            }

            row.style.borderRadius = "12px";
            row.style.overflow = "hidden";
            row.style.backgroundColor = (visibleRowIndex % 2 === 0) ? "#FFFFFF" : "#fdf5ec";
            visibleRowIndex++;

            if (radio) radio.style.display = "none"; 
            if (labelCell) labelCell.style.display = "none"; 
            
            if (contentCell) {
                contentCell.style.display = "table-cell";
                contentCell.style.verticalAlign = "middle";
                contentCell.style.padding = "10px 10px 10px 50px";
            }
        }

        if (ansCell) {
            ansCell.style.display = "table-cell";
            ansCell.style.verticalAlign = "middle";
            ansCell.innerHTML = "";

            const isUserCorrect = (enteredCorrect && (enteredCorrect === idx || enteredCorrect === val));
            
            const isUserWrong = (enteredWrong && (enteredWrong === idx || enteredWrong === val));

            const isActuallyCorrectRow = (idx === correctIndexStr);

            if (isUserCorrect) {
                if (!isImageBased) row.style.backgroundColor = "#7A96DF"; 
                ansCell.innerHTML = `<img src="${ICONS.CorrectGif}" alt="Correct" style="vertical-align: middle;" />`;
            } 
            else if (isUserWrong) {
                if (!isImageBased) row.style.backgroundColor = "#7A96DF";
                ansCell.innerHTML = `<img src="${ICONS.WrongGif}" alt="Wrong" style="vertical-align: middle;" />`;
            } 
            else if ((enteredWrong || (!enteredCorrect && !enteredWrong)) && isActuallyCorrectRow) {
                ansCell.innerHTML = `<img src="${ICONS.ShouldBeGif}" alt="Should Be" style="vertical-align: middle;" />`;
            }
        }
    });
};

// ============================================================================
// TYPE B (Boolean) - UPDATED BACKGROUNDS
// ============================================================================
const markTypeB = (doc: Document, enteredCorrect: string, enteredWrong: string) => {
    const rows = doc.querySelectorAll("tr[id^='KSD_TR_']");
    const infoVal = (doc.getElementById("KSD_MW_Info") as HTMLInputElement)?.value;
    
    const parts = infoVal ? infoVal.split("|") : [];
    const correctIndex = parts[4] || null; // Index 4 is correct index for Type B (usually stays 0-based from XML)
    const optionImageType = parts.length >= 9 ? parseInt(parts[8]) : 0;

    // Define Backgrounds
    let bgNormal = "";

    if (optionImageType > 0) {
        bgNormal = `url('${ASSETS_BASE_URL}Image/Questions/EL_B/regular_fillblanks_n.png')`;
    }

    // Divider Bar
    const divider = doc.getElementById("KSD_DIVIDERBAR");
    if (divider && optionImageType > 0) {
        divider.style.backgroundImage = `url('${ASSETS_BASE_URL}Image/Questions/EL_B/divider_bar.png')`;
        divider.style.backgroundRepeat = "no-repeat";
    }

    rows.forEach((row) => {
        const rowId = row.id; 
        const index = rowId.substring(7); // "0" or "1"
        
        // Convert 0-based DOM index to 1-based logic for comparison
        // Row 0 becomes "1", Row 1 becomes "2"
        const matchValue = (parseInt(index) + 1).toString();

        const optionCell = row.querySelector("td[id^='KSD_TDN_']") || row.querySelector("td[id^='KSD_TD_']");
        const ansCell = row.querySelector("td[id^='KSD_TDANS_']") as HTMLElement;
        
        // Determine if this row was selected by the user
        // Compare the 1-based matchValue against the user's response
        const isUserSelection = (matchValue === enteredCorrect) || (matchValue === enteredWrong);

        // Apply Background Logic
        if (optionCell && bgNormal) {
            const el = optionCell as HTMLElement;
            
            // Use Selected BG if user picked this, otherwise Normal BG
            const finalBg = bgNormal;

            el.style.backgroundImage = finalBg;
            el.style.backgroundRepeat = "no-repeat";
            el.style.backgroundPosition = "center";
            el.style.backgroundColor = "transparent";
            el.style.backgroundSize = "contain";
            el.style.textAlign = "center";
            el.style.verticalAlign = "middle";
            el.style.height = "40px"; 
            el.style.width = "150px";
            el.style.cursor = "default";
            el.style.paddingTop = "20px";
        }

        if (!ansCell) return;
        ansCell.style.display = ""; 
        ansCell.innerHTML = "";     

        // Logic for Marks:
        if (enteredCorrect && !enteredWrong) {
            // User Correct: Compare using 1-based matchValue
            if (matchValue === enteredCorrect) {
                ansCell.innerHTML = `<img src="${ICONS.CorrectGif}" alt="Correct" style="vertical-align: middle;" />`;
            }
        } 
        else if (enteredWrong) {
            // User Wrong: Compare using 1-based matchValue
            if (matchValue === enteredWrong) {
                ansCell.innerHTML = `<img src="${ICONS.WrongGif}" alt="Wrong" style="vertical-align: middle;" />`;
            } 
            // For "Should Be", we compare against correctIndex (typically 0-based from hidden info)
            else if (index === correctIndex) {
                ansCell.innerHTML = `<img src="${ICONS.ShouldBeGif}" alt="Should Be" style="vertical-align: middle;" />`;
            }
        } 
        else {
            // Skipped
            if (index === correctIndex) {
                ansCell.innerHTML = `<img src="${ICONS.ShouldBeGif}" alt="Should Be" style="vertical-align: middle;" />`;
            }
        }
    });
};

const markTypeN = (doc: Document, enteredCorrect: string, enteredWrong: string, blankAns: string) => {
    removeElements(doc, ["KSD_DIVIDERBAR", "KSD_NUMPAD", "KSD_W_PAD"]);

    const splitData = (str: string) => {
        const parts = (str || "").split(",");
        if (parts.length === 2 && str.includes("~")) {
            return { blankStr: parts[0], carryStr: parts[1] };
        }
        return { blankStr: str, carryStr: str };
    };

    const parseMap = (str: string): Record<number, string> => {
        const map: Record<number, string> = {};
        if (!str) return map;

        if (str.includes("~")) {
            const parts = str.split("~");
            parts.forEach(part => {
                const match = part.match(/^(\d{2})(.*)$/);
                if (match) {
                    map[parseInt(match[1], 10)] = match[2];
                } else if (/^\d+$/.test(part)) {
                    map[parseInt(part, 10)] = "";
                }
            });
        } else {
            const parts = str.split(/[,|]/);
            parts.forEach((val, idx) => {
                if(val.trim() !== "") map[idx] = val.trim();
            });
        }
        return map;
    };

    const cor = splitData(enteredCorrect);
    const wrong = splitData(enteredWrong);
    const skipped = splitData(blankAns);

    const blankCorMap = parseMap(cor.blankStr);
    const blankWrongMap = parseMap(wrong.blankStr);
    
    const carryCorMap = parseMap(cor.carryStr);
    const carryWrongMap = parseMap(wrong.carryStr);

    const bwVal = (doc.getElementById("KSD_BW") as HTMLInputElement)?.value;
    const allCorrectArr = bwVal ? bwVal.split("|") : [];

    const blanks = doc.querySelectorAll("input[id^='KSD_BLANK_']");
    blanks.forEach((input) => {
        const el = input as HTMLInputElement;
        const parent = el.parentElement;
        if (!parent) return;

        const idMatch = el.id.match(/_(\d+)$/);
        const index = idMatch ? parseInt(idMatch[1], 10) : -1;
        if (index === -1) return;

        const correctVal = allCorrectArr[index] || "";
        parent.style.position = "relative";

        if (blankCorMap.hasOwnProperty(index)) {
            el.style.display = "none";
            
            const span = doc.createElement("span");
            span.style.cssText = "color: green; font-family: Verdana; font-size: 20pt; border-bottom: 2px solid red; display: inline-block; min-width: 20px; text-align: center;";
            span.innerHTML = blankCorMap[index];
            parent.appendChild(span);
        } 
        else if (blankWrongMap.hasOwnProperty(index)) {
            el.style.display = "none";
            
            const wrongVal = blankWrongMap[index];
            const span = doc.createElement("span");
            span.style.cssText = "font-family: Verdana; font-size: 20pt; border-bottom: 2px solid red; display: inline-block;";
            
            span.innerHTML = `
                <span style="color: red; text-decoration: line-through; background-color: yellow;">${wrongVal}</span>&nbsp;
                <span style="color: green;">${correctVal}</span>
            `;
            parent.appendChild(span);
        } 
        else {
            el.style.display = "inline-block"; 
            el.style.backgroundColor = "white"; 
            el.style.border = "1px dashed black";
            el.value = "";
            
            const span = doc.createElement("span");
            span.style.cssText = "color: green; font-family: Verdana; font-size: 20pt; margin-left: 5px;";
            span.innerHTML = `&nbsp;${correctVal}`;
            parent.appendChild(span);
        }
    });

    const carries = doc.querySelectorAll("input[id^='TXT_CARRY_']");
    carries.forEach((input) => {
        const el = input as HTMLInputElement;
        const parent = el.parentElement;
        if (!parent) return;

        const idMatch = el.id.match(/_(\d+)$/);
        const index = idMatch ? parseInt(idMatch[1], 10) : -1;
        if (index === -1) return;

        const correctVal = el.name || "";
        if (correctVal === "-") return;

        if (carryCorMap.hasOwnProperty(index)) {
            el.style.display = "none";
            const span = doc.createElement("span");
            span.style.cssText = "color: green; font-family: Verdana; font-size: 15pt; margin-left: 5px;";
            span.innerHTML = carryCorMap[index];
            parent.appendChild(span);
        } 
        else if (carryWrongMap.hasOwnProperty(index)) {
            el.style.display = "none";
            const wrongVal = carryWrongMap[index];
            const span = doc.createElement("span");
            span.style.cssText = "font-family: Verdana; font-size: 15pt; margin-left: 5px;";
            
            span.innerHTML = `
                <span style="color: red; text-decoration: line-through; background-color: yellow;">${wrongVal}</span>&nbsp;
                <span style="color: green;">${correctVal}</span>
            `;
            parent.appendChild(span);
        } 
        else {
            el.style.display = "inline-block";
            el.style.backgroundColor = "white";
            el.style.border = "1px dashed black";
            el.value = "";

            if (correctVal) {
                const span = doc.createElement("span");
                span.style.cssText = "color: black; font-family: Verdana; font-size: 15pt; margin-left: 20px;";
                span.innerHTML = correctVal;
                parent.appendChild(span);
            }
        }
    });
};

const markTypeF = (doc: Document, enteredCorrect: string, enteredWrong: string, enteredBlank: string) => {
    const divider = doc.getElementById("KSD_DIVIDERBAR");
    if (divider) {
        const rightPanel = divider.nextElementSibling;
        if (rightPanel) rightPanel.remove();
        divider.remove();
    }
    removeElements(doc, ["KSD_DIVIDERBAR", "KSD_NUMPAD"]);
    const bwVal = (doc.getElementById("KSD_BW") as HTMLInputElement)?.value;
    const correctAnswers = bwVal ? bwVal.split("|") : [];
    const corIndices = parseIndicesRobust(enteredCorrect);
    const wrongMap = parseValuesRobust(enteredWrong);
    const isAllPerfect = (enteredCorrect !== "" && enteredWrong === "" && enteredBlank === "");
    const blanks = doc.querySelectorAll("input[id^='KSD_BLANK_']");
    blanks.forEach((input) => {
        const el = input as HTMLInputElement;
        const parent = el.parentElement;
        if (!parent) return;
        const idMatch = el.id.match(/KSD_BLANK_(\d+)/);
        const index = idMatch ? parseInt(idMatch[1], 10) : -1;
        if (index === -1) return;
        el.style.display = "none"; 
        const correctVal = correctAnswers[index] || "";
        if (isAllPerfect || corIndices.includes(index)) {
            parent.style.borderBottom = "2px solid red"; 
            
            const span = doc.createElement("span");
            
            span.style.cssText = `
                display: inline-flex; 
                align-items: center; 
                color: green; 
                font-family: Verdana;
                font-weight: normal;
            `;
            
            span.innerHTML = `${correctVal}&nbsp;&nbsp;<img src="${ICONS.CorrectGif}" style="vertical-align: middle; height: 20px;" alt="Correct Mark" />`;
            parent.appendChild(span);
        }
        else if (wrongMap.hasOwnProperty(index)) {
            parent.style.borderBottom = "2px solid red"; 
            const wrongVal = wrongMap[index];
            const span = doc.createElement("span");
            span.style.cssText = "font-family: Verdana;";
            span.innerHTML = `
                <span style="color: red; text-decoration: line-through;">${wrongVal}</span>&nbsp;
                <span style="color: green;">${correctVal}</span>
            `;
            parent.appendChild(span);
        } 
        else {
            parent.style.borderBottom = "2px solid black"; 
            const span = doc.createElement("span");
            span.style.cssText = "color: red; font-weight: bold; font-family: Verdana;"; 
            span.innerHTML = `&nbsp;${correctVal}`;
            parent.appendChild(span);
        }
    });
};

const markTypeW = (doc: Document, userAns: string, wrongAns: string, blankAns: string, correctAnsStr: string) => {
    removeElements(doc, ["KSD_DIVIDERBAR", "KSD_NUMPAD", "KSD_W_PAD"]);
    const textarea = doc.getElementById("KSD_W") as HTMLTextAreaElement | HTMLInputElement;
    const tdAns0 = doc.getElementById("KSD_TDANS_0");
    const tdAns1 = doc.getElementById("KSD_TDANS_1");
    const tdAnsOk = doc.getElementById("KSD_TDANSOK");
    if (!textarea) return;
    textarea.disabled = true;
    textarea.style.height = "35px";
    if (!correctAnsStr) {
        const caVal = (doc.getElementById("KSD_MW_CA") as HTMLInputElement)?.value;
        if (caVal) correctAnsStr = caVal.split("^^")[0];
    }
    if (userAns && !wrongAns) {
        textarea.value = userAns;
        if(tdAns0) {
            tdAns0.style.display = "";
            tdAns0.innerHTML = `<img src="${ICONS.CorrectGif}" />`;
        }
    } 
    else if (wrongAns) {
        textarea.value = wrongAns;
        if(tdAns0) {
            tdAns0.style.display = "";
            tdAns0.innerHTML = `<img src="${ICONS.WrongGif}" />`;
        }
        if(tdAns1) {
            tdAns1.style.display = "";
            tdAns1.innerHTML = `<img src="${ICONS.ShouldBeGif}" />`;
        }
        if(tdAnsOk) {
            tdAnsOk.style.display = "";
            tdAnsOk.style.color = "green";
            tdAnsOk.style.fontFamily = "Verdana";
            tdAnsOk.innerHTML = correctAnsStr;
        }
    } 
    else {
        if(tdAns0) {
            tdAns0.style.display = "";
            tdAns0.innerHTML = `<img src="${ICONS.WrongGif}" />`; 
        }
        if(tdAns1) {
            tdAns1.style.display = "";
            tdAns1.innerHTML = `<img src="${ICONS.ShouldBeGif}" />`;
        }
        if(tdAnsOk) {
            tdAnsOk.style.display = "";
            tdAnsOk.style.color = "green";
            tdAnsOk.style.fontFamily = "Verdana";
            tdAnsOk.innerHTML = correctAnsStr;
        }
    }
};

const markTypeC = (doc: Document, enteredCorrect: string, enteredWrong: string) => {
    let selectedCorrect: number[] = [];
    let selectedWrong: number[] = [];
    let definitiveCorrectIndices: number[] = [];
    const infoVal = (doc.getElementById("KSD_MW_Info") as HTMLInputElement)?.value;
    const infoParts = infoVal ? infoVal.split("|") : [];
    const correctStr = infoParts.length > 4 ? infoParts[4] : "";
    const toIndex = (val: string) => {
        if (!val) return -1;
        if (/^[0-9]+$/.test(val)) return parseInt(val, 10);
        if (/^[A-Z]$/.test(val)) return val.charCodeAt(0) - 65;
        return -1;
    };
    for (let char of correctStr) {
        definitiveCorrectIndices.push(toIndex(char));
    }
    if (enteredCorrect) {
        if (enteredCorrect.includes("#")) {
            const parts = enteredCorrect.split("#");
            if (parts.length > 1) {
                selectedCorrect = parts[1].split("~").map(toIndex).filter(i => i !== -1);
            }
        } else {
            selectedCorrect = enteredCorrect.split(/\||~|,/).map(toIndex).filter(i => i !== -1);
        }
    }
    if (enteredWrong) {
        selectedWrong = enteredWrong.split(/\||~|,/).map(toIndex).filter(i => i !== -1);
    }
    const optionImageType = infoParts.length >= 9 ? parseInt(infoParts[8]) : 0;
    let bgNormal = "";
    let bgSelected = "";
    if (TYPE_C_BGS[optionImageType]) {
        bgNormal = `url('${ASSETS_BASE_URL}Image/Questions/EL_C/${TYPE_C_BGS[optionImageType].n}')`;
        bgSelected = `url('${ASSETS_BASE_URL}Image/Questions/EL_C/${TYPE_C_BGS[optionImageType].d}')`;
    }
    const divider = doc.getElementById("KSD_DIVIDERBAR");
    if (divider && optionImageType >= 2) {
        divider.style.backgroundImage = `url('${ASSETS_BASE_URL}Image/Questions/EL_C/divider_bar.png')`;
        divider.style.backgroundRepeat = "no-repeat";
    }
    if (optionImageType >= 2) {
        const table = doc.getElementById("KSD_TABLE");
        if (table) {
            table.setAttribute("cellspacing", "10");
            table.setAttribute("cellpadding", "5");
        }
    }
    const rows = doc.querySelectorAll("tr[id^='KSD_TR_']");
    rows.forEach((row) => {
        const rowId = row.id; 
        const index = parseInt(rowId.substring(7), 10); 
        const ansCell = (row.querySelector("td[id^='KSD_TDANS_']") || row.querySelector("td[id^='TDANS_']")) as HTMLElement;
        const optionCell = row.querySelector("td[id^='KSD_TD_']") as HTMLElement;
        if (!ansCell) return;
        const isUserSelected = selectedCorrect.includes(index) || selectedWrong.includes(index);
        if (optionCell && bgNormal) {
            const finalBg = isUserSelected ? bgSelected : bgNormal;
            optionCell.style.backgroundImage = finalBg;
            optionCell.style.backgroundRepeat = "no-repeat";
            optionCell.style.backgroundColor = "transparent";
            optionCell.style.backgroundSize = "100% 100%"; 
            if (optionImageType >= 2) optionCell.style.padding = "10px";
        }
        ansCell.style.display = "";
        ansCell.innerHTML = "";
        if (selectedCorrect.includes(index)) {
            ansCell.innerHTML = `<img src="${ICONS.CorrectGif}" alt="Correct" style="vertical-align: middle;" />`;
        } 
        else if (selectedWrong.includes(index)) {
            ansCell.innerHTML = `<img src="${ICONS.WrongGif}" alt="Wrong" style="vertical-align: middle;" />`;
        } 
        else if (definitiveCorrectIndices.includes(index)) {
            ansCell.innerHTML = `<img src="${ICONS.ShouldBeGif}" alt="Should Be" style="vertical-align: middle;" />`;
        }
    });
};

const markTypeD = (doc: Document, enteredCorrect: string, enteredWrong: string) => {
    const selects = doc.querySelectorAll("select[id='KSD_SELECT']");
    const parseMap = (str: string) => str ? str.split("~").reduce((acc, s) => {
        acc[parseInt(s.substring(0, 2))] = s.substring(2);
        return acc;
    }, {} as Record<number, string>) : {};
    const corMap = parseMap(enteredCorrect);
    const wrongMap = parseMap(enteredWrong);
    selects.forEach((select, index) => {
        const el = select as HTMLSelectElement;
        const parent = el.parentElement;
        if(!parent) return;
        el.style.display = "none";
        const span = doc.createElement("span");
        span.style.margin = "3px";
        const correctOption = el.querySelector("option[value='1']");
        const correctText = correctOption ? correctOption.textContent : "";
        if (corMap.hasOwnProperty(index)) {
            span.style.color = "green";
            span.innerHTML = `${correctText} <img src="${ICONS.CorrectGif}" style="vertical-align: middle;" />`;
            span.style.borderBottom = "2px solid red";
        } else if (wrongMap.hasOwnProperty(index)) {
            const wrongText = wrongMap[index];
            span.innerHTML = `
                <span style="color: red; text-decoration: line-through;">${wrongText}</span>&nbsp;
                <span style="color: green;">${correctText}</span>
            `;
            span.style.borderBottom = "2px solid red";
        } else {
            span.style.color = "green";
            span.innerHTML = `${correctText}`;
            span.style.borderBottom = "2px solid red";
        }
        parent.insertBefore(span, el);
    });
};

const markTypeM = (doc: Document, enteredCorrect: string, enteredWrong: string) => {
    const tds = doc.querySelectorAll("td[id^='TDANS_']");
    if (enteredCorrect && !enteredWrong) {
        tds.forEach(tdNode => {
            const td = tdNode as HTMLElement;
            td.style.display = "";
            td.innerHTML = `<img src="${ICONS.CorrectGif}" />`;
        });
    } else if (enteredCorrect && enteredWrong) {
        const lhs = enteredCorrect.split("~");
        const rhs = enteredWrong.split("~");
        tds.forEach((tdNode, index) => {
            const td = tdNode as HTMLElement;
            td.style.display = "";
            if (lhs[index] === rhs[index]) {
                td.innerHTML = `<img src="${ICONS.CorrectGif}" />`;
            } else {
                td.innerHTML = `<img src="${ICONS.WrongGif}" />`;
            }
        });
    } else {
        tds.forEach(tdNode => {
            const td = tdNode as HTMLElement;
            td.style.display = "";
            td.innerHTML = `<img src="${ICONS.CorrectGif}" />`;
        });
    }
};

const markTypeL = (doc: Document, enteredCorrect: string, enteredWrong: string) => {
    const rows = doc.querySelectorAll("#KSD_TABLE tbody tr");
    const corIndices = parseIndicesRobust(enteredCorrect);
    const wrongIndices = parseIndicesRobust(enteredWrong);
    rows.forEach((row) => {
        const id = row.id.substring(3);
        const td = row.querySelector("td[id^='TDANS_']") as HTMLElement;
        if(!td) return;
        td.style.display = "";
        if (corIndices.includes(parseInt(id))) {
            td.innerHTML = `<img src="${ICONS.CorrectGif}" />`;
        } else if (wrongIndices.includes(parseInt(id))) {
            td.innerHTML = `<img src="${ICONS.WrongGif}" />`;
        } else {
            td.innerHTML = `<img src="${ICONS.ShouldBeGif}" />`;
        }
    });
};

// ============================================================================
// TYPE I (Identifying Words) - FIXED VALUE vs INDEX COLLISION
// ============================================================================
const markTypeI = (doc: Document, enteredCorrect: string, enteredWrong: string, blankAns: string) => {
    
    // 1. Helper: Parse "1|3|5" or "1~3~5" into String Array
    const parseValues = (str: string): string[] => {
        if (!str) return [];
        const delimiter = str.includes("|") ? "|" : "~";
        return str.split(delimiter).map(s => s.trim()).filter(s => s !== "");
    };

    const corArr = parseValues(enteredCorrect); 
    const wrongArr = parseValues(enteredWrong);
    const missedArr = parseValues(blankAns);

    // 2. Detect Skipped State
    const isSkipped = (enteredCorrect === "" && enteredWrong === "");

    // 3. Hydration (Create elements if missing)
    const container = doc.getElementById("KSD_TDTYPEI");
    const infoInput = doc.getElementById("KSD_MW_Info") as HTMLInputElement;
    let words = doc.querySelectorAll(".clickable-item, .word-option");

    if (container && words.length === 0 && infoInput && infoInput.value) {
        try {
            const parts = infoInput.value.split("|");
            const count = parseInt(parts[0], 10);
            let htmlBuffer = "";
            let optionIndex = 1; 

            for (let i = 1; i <= count; i++) {
                const raw = parts[i];
                if (!raw) continue;
                if (raw === "0N<NEW LINE>") {
                    htmlBuffer += "<br/>";
                } else {
                    const text = raw.substring(2); 
                    htmlBuffer += `
                        <span id="spanWordOption${optionIndex}" class="word-option">
                            &nbsp;${text}&nbsp;
                        </span>
                    `;
                    optionIndex++;
                }
            }
            container.innerHTML = htmlBuffer;
            words = doc.querySelectorAll(".word-option");
            container.style.border = "none";
            container.style.textAlign = "center";
            container.style.lineHeight = "2.5";
        } catch (e) {
            console.error("Error hydrating Type I question:", e);
        }
    }

    // 4. Calculate Correct Indices for Skipped State
    const correctIndicesIfSkipped: number[] = [];
    if (isSkipped && infoInput && infoInput.value) {
        const parts = infoInput.value.split("|");
        const count = parseInt(parts[0], 10);
        let visualIndex = 1;
        for (let i = 1; i <= count; i++) {
            const optStr = parts[i] || "";
            if (optStr === "0N<NEW LINE>") continue;
            if (optStr.length >= 2 && optStr[1] === "Y") {
                correctIndicesIfSkipped.push(visualIndex);
            }
            visualIndex++;
        }
    }

    // 5. Marking Logic
    words.forEach((wordNode) => {
        const el = wordNode as HTMLElement;
        let index = -1;

        const dataId = el.getAttribute("data-id");
        if (dataId) {
            index = parseInt(dataId, 10);
        } else if (el.id.startsWith("spanWordOption")) {
            index = parseInt(el.id.replace("spanWordOption", ""), 10);
        }

        if (index === -1) return;

        // Base Styles
        el.style.display = "inline-block";
        el.style.padding = "2px 5px";
        el.style.margin = "0 4px";
        el.style.cursor = "default";
        el.style.fontSize = "18pt";
        el.style.fontFamily = "Arial, sans-serif";

        if (isSkipped) {
            // SKIPPED: Green BG for correct items
            if (correctIndicesIfSkipped.includes(index)) {
                el.style.backgroundColor = "#006600"; 
                el.style.color = "white";
                el.style.border = "none";
            } else {
                el.style.backgroundColor = "transparent";
                el.style.color = "black";
                el.style.border = "2px solid transparent";
            }
        } 
        else {
            // ATTEMPTED
            const textValue = el.textContent?.trim() || "";
            
            // Legacy logic pads indices < 10 (e.g. "01", "05")
            // We check 'indexPad' (e.g. "05") and 'textValue' (e.g. "8")
            // We DO NOT check raw 'indexStr' ("5") to avoid collision with value "5"
            const indexPad = index < 10 ? "0" + index : index.toString();

            const isCorrect = corArr.includes(indexPad) || corArr.includes(textValue);
            const isWrong = wrongArr.includes(indexPad) || wrongArr.includes(textValue);
            const isMissed = missedArr.includes(indexPad) || missedArr.includes(textValue);

            if (isCorrect) {
                el.style.border = "2px solid #77c84b"; // Green
                if (!el.querySelector("img")) {
                    el.innerHTML += `&nbsp;<img src="${ICONS.Correct}" width="17" style="vertical-align: middle;" />`;
                }
            } 
            else if (isWrong) {
                el.style.border = "2px solid #e21818"; // Red
                if (!el.querySelector("img")) {
                    el.innerHTML += `&nbsp;<img src="${ICONS.Wrong}" width="17" style="vertical-align: middle;" />`;
                }
            } 
            else if (isMissed) {
                el.style.border = "2px solid #757575"; // Grey
            } 
            else {
                el.style.border = "2px solid transparent";
            }
        }
    });
};
